import React, { useState, useEffect } from 'react';

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.navigator.standalone;
}

function isAndroid() {
  return /android/i.test(navigator.userAgent);
}

function isInStandaloneMode() {
  return window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;
}

export default function PWAPrompt({ onDismiss }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [platform, setPlatform] = useState(null);

  useEffect(() => {
    if (isInStandaloneMode()) return;
    if (sessionStorage.getItem('pwa-dismissed')) return;

    if (isIOS()) {
      setPlatform('ios');
      setShow(true);
    } else if (isAndroid()) {
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setPlatform('android');
        setShow(true);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }
  }, []);

  const dismiss = () => {
    sessionStorage.setItem('pwa-dismissed', '1');
    setShow(false);
    if (onDismiss) onDismiss();
  };

  const installAndroid = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') sessionStorage.setItem('pwa-dismissed', '1');
    setDeferredPrompt(null);
    setShow(false);
    if (onDismiss) onDismiss();
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9998,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      padding: '0 0 env(safe-area-inset-bottom, 24px)',
    }}>
      <div style={{
        background: '#0c1018',
        border: '1px solid rgba(245,158,11,.3)',
        borderRadius: '20px 20px 0 0',
        padding: '24px 24px 32px',
        width: '100%',
        maxWidth: 480,
        boxShadow: '0 -8px 40px rgba(245,158,11,.12)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
          <img src="/icon-192.png" alt="AKKKANTO.it" style={{ width: 56, height: 56, borderRadius: 14, border: '2px solid rgba(245,158,11,.3)' }} />
          <div>
            <div style={{ fontSize: 18, fontWeight: 900, color: '#f59e0b', letterSpacing: -0.5 }}>AKKKANTO.it</div>
            <div style={{ fontSize: 11, color: '#8892a4', marginTop: 2 }}>Akkkanto.it powered Alfa Srl</div>
          </div>
          <button onClick={dismiss} style={{ marginLeft: 'auto', background: 'rgba(255,255,255,.06)', border: 'none', borderRadius: 50, width: 30, height: 30, color: '#8892a4', fontSize: 18, cursor: 'pointer', lineHeight: 1, flexShrink: 0 }}>✕</button>
        </div>

        <div style={{ fontSize: 16, fontWeight: 700, color: '#e8eaf0', marginBottom: 6 }}>
          📲 Installa l'app sul tuo telefono
        </div>
        <div style={{ fontSize: 13, color: '#8892a4', lineHeight: 1.5, marginBottom: 20 }}>
          Accedi in un tap dalla schermata home, come un'app vera.
        </div>

        {platform === 'ios' && (
          <>
            {/* Step by step iOS */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {[
                { n: '1', icon: '⬆️', testo: 'Tocca il pulsante', bold: 'Condividi', sub: 'in basso al centro del browser (freccia che punta su)' },
                { n: '2', icon: '📋', testo: 'Scorri in basso e tocca', bold: '"Aggiungi a Home"', sub: 'con il simbolo + quadrato' },
                { n: '3', icon: '✅', testo: 'Tocca', bold: '"Aggiungi"', sub: 'in alto a destra — fatto!' },
              ].map(s => (
                <div key={s.n} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, background: 'rgba(245,158,11,.06)', border: '1px solid rgba(245,158,11,.15)', borderRadius: 12, padding: '12px 14px' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#f59e0b', color: '#000', fontWeight: 900, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{s.n}</div>
                  <div>
                    <div style={{ fontSize: 13, color: '#e8eaf0' }}>{s.icon} {s.testo} <strong style={{ color: '#f59e0b' }}>{s.bold}</strong></div>
                    <div style={{ fontSize: 11, color: '#8892a4', marginTop: 3 }}>{s.sub}</div>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={dismiss} style={{ width: '100%', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.12)', borderRadius: 12, padding: '13px', color: '#8892a4', fontSize: 13, cursor: 'pointer' }}>
              Forse dopo
            </button>
          </>
        )}

        {platform === 'android' && (
          <>
            <div style={{ background: 'rgba(245,158,11,.06)', border: '1px solid rgba(245,158,11,.15)', borderRadius: 12, padding: '14px', marginBottom: 16, fontSize: 13, color: '#e8eaf0', lineHeight: 1.6 }}>
              📲 Tocca <strong style={{ color: '#f59e0b' }}>"Installa App"</strong> qui sotto — in 1 secondo l'icona apparirà nella tua schermata home.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={installAndroid} style={{ flex: 1, background: 'linear-gradient(135deg,#f59e0b,#d97706)', border: 'none', borderRadius: 12, padding: '14px', color: '#000', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
                📲 Installa App
              </button>
              <button onClick={dismiss} style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.12)', borderRadius: 12, padding: '14px 18px', color: '#8892a4', fontSize: 13, cursor: 'pointer' }}>
                No
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
